import { Injectable, inject, signal } from '@angular/core';
import MarkdownIt from 'markdown-it';

import { CANONICAL_AI_ENGINEERING_COURSE_ID, normalizeCourseId } from '../course-id.aliases';
import { CONTENT_PACKAGE_SOURCE } from './content-package-source';

export type GlossaryLanguage = 'de' | 'en';

export interface GlossaryCourseReference {
  courseId: string;
  moduleId: string;
}

export interface GlossaryManifestEntry {
  file: string;
  id: string;
  key: string;
  language: 'DE' | 'EN';
  term: string;
  shortDefinition: string;
  status: 'draft' | 'review' | 'published' | 'archived';
  aliases: string[];
  moduleIds: string[];
  courseRefs?: GlossaryCourseReference[];
  tags: string[];
  relatedEntryKeys: string[];
  relatedEntryIds: string[];
  body: string;
}

interface ContentManifest {
  glossary?: {
    entries?: GlossaryManifestEntry[];
  };
}

const glossaryMarkdown = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
});

@Injectable({ providedIn: 'root' })
export class GlossaryService {
  private readonly packageSource = inject(CONTENT_PACKAGE_SOURCE);
  readonly entries = signal<ReadonlyArray<GlossaryManifestEntry>>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly openRequest = signal<{ entryId: string; requestId: number } | null>(null);

  private loadPromise?: Promise<void>;
  private nextRequestId = 0;

  requestOpen(entryId: string): void {
    this.openRequest.set({
      entryId,
      requestId: ++this.nextRequestId,
    });
  }

  load(): Promise<void> {
    if (this.entries().length > 0) {
      return Promise.resolve();
    }
    if (!this.loadPromise) {
      this.loadPromise = this.fetchManifest();
    }
    return this.loadPromise;
  }

  entriesForLanguage(language: GlossaryLanguage): ReadonlyArray<GlossaryManifestEntry> {
    const manifestLanguage = language.toUpperCase();
    return this.entries()
      .filter(
        (entry) =>
          entry.language === manifestLanguage
          && (entry.status === 'published' || entry.status === 'review'),
      )
      .sort((a, b) => a.term.localeCompare(b.term, language));
  }

  entryForLanguage(entryId: string, language: GlossaryLanguage): GlossaryManifestEntry | null {
    return this.entriesForLanguage(language).find((entry) => entry.id === entryId) ?? null;
  }

  moduleIdsForCourse(
    entry: GlossaryManifestEntry,
    courseId: string | null | undefined,
  ): ReadonlyArray<string> {
    if (!courseId?.trim()) {
      return [];
    }

    const normalizedCourseId = normalizeCourseId(courseId);
    if (Object.prototype.hasOwnProperty.call(entry, 'courseRefs')) {
      return (entry.courseRefs ?? [])
        .filter((reference) => normalizeCourseId(reference.courseId) === normalizedCourseId)
        .map((reference) => reference.moduleId);
    }

    return normalizedCourseId === CANONICAL_AI_ENGINEERING_COURSE_ID
      ? entry.moduleIds
      : [];
  }

  renderBody(entry: GlossaryManifestEntry): string {
    const withoutDuplicateTitle = entry.body.replace(/^#\s+.+?(?:\r?\n)+/, '');
    const normalizedWikiLinks = withoutDuplicateTitle.replace(
      /!?\[\[([^\]|#]+)(?:#[^\]|]+)?(?:\|([^\]]+))?]]/g,
      (_match, target: string, alias?: string) => alias?.trim() || target.trim(),
    );
    return glossaryMarkdown.render(normalizedWikiLinks).trim();
  }

  private async fetchManifest(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const response = await this.packageSource.readJson<ContentManifest>('content-manifest.json');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const manifest = response.value as ContentManifest;
      const entries = manifest.glossary?.entries;
      if (!Array.isArray(entries)) {
        throw new Error('Manifest enthält keinen Glossarbereich.');
      }
      this.entries.set(entries);
    } catch (error) {
      this.error.set(
        `Glossar konnte nicht geladen werden: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      this.loadPromise = undefined;
    } finally {
      this.loading.set(false);
    }
  }
}
